<template>
    <header class="flex h-16 shrink-0 items-center gap-2 border-b border-default px-3 sm:px-4">
        <button
            class="hover-animation cursor-pointer lg:hidden"
            :aria-label="$t('navigation.menu')"
            @click="emit('toggle-menu')"
        >
            <AppIcon name="bars" class="size-9" />
        </button>

        <div class="ml-auto flex items-center gap-2">
            <SwitchUnit />

            <UButton
                color="neutral"
                variant="ghost"
                :label="locale === 'sl' ? 'EN' : 'SL'"
                @click="toggleLocale"
            />

            <UDropdownMenu :items="userItems">
                <button
                    class="hover-animation flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 sm:max-w-96 sm:border sm:border-default sm:bg-default sm:shadow-xs"
                >
                    <AppIcon name="user" class="size-9" />
                    <span class="hidden min-w-0 text-left font-semibold sm:block">{{ userLabel }}</span>
                    <UIcon name="i-lucide-chevron-down" class="ml-1 hidden size-4 shrink-0 stroke-[3] text-primary sm:block" />
                </button>
            </UDropdownMenu>
        </div>
    </header>
</template>

<script setup lang="ts">
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRouter } from 'vue-router'

    import { InternalPages } from '@/enums/allowedPages'
    import AppIcon from '@/components/AppIcon.vue'
    import SwitchUnit from '@/layouts/components/SwitchUnit.vue'
    import { parseErrorAuth } from '@/libs/error'
    import { PrintError } from '@/libs/toast'
    import { useAppStore } from '@/stores/app'
    import { useFetchStore } from '@/stores/fetch'

    const emit = defineEmits<(e: 'toggle-menu') => void>()

    // data
    const { t, locale } = useI18n()
    const appStore = useAppStore()
    const fetchStore = useFetchStore()
    const router = useRouter()

    // computed
    const userData = computed(() => {
        return appStore.getUserData()
    })

    const userLabel = computed(() => {
        return `${userData.value.firstname} ${userData.value.lastname}`.trim()
    })

    // methods
    const checkAllowedPages = (page: number) => {
        return appStore.getAllowedPages().includes(page)
    }

    const toggleLocale = () => {
        const next = locale.value === 'sl' ? 'en' : 'sl'
        locale.value = next
        localStorage.setItem('locale', next)
    }

    const logout = async() => {
        const { response, data, error } = await fetchStore.fetch('/api/auth/v1/logout').get().json()
        if (response.value?.status === 200) {
            appStore.setLogout()
            await router.push({ name: 'login' })
        } else {
            PrintError(parseErrorAuth(error.value ?? data.value, t('errors.on_logout')))
        }
    }

    const userItems = computed(() => {
        const links = [
            { label: t('navigation.profile'), icon: 'i-lucide-user', to: { name: 'profil' }, page: InternalPages.Profil },
            { label: t('navigation.change_password'), icon: 'i-lucide-key-round', to: { name: 'change-password' }, page: InternalPages.Profil },
            { label: t('navigation.add_manager'), icon: 'i-lucide-building-2', to: { name: 'add-manager' }, page: InternalPages.AddManager }
        ].filter(link => checkAllowedPages(link.page))

        return [
            links.map(({ label, icon, to }) => ({ label, icon, to })),
            [{
                label: t('navigation.logout'),
                icon: 'i-lucide-log-out',
                color: 'error' as const,
                onSelect: logout
            }]
        ].filter(group => group.length > 0)
    })
</script>
